import { useCallback, useEffect, useState } from 'react';
import { rage, useObservable } from '~/rage';
import { Character } from '../Character';
import { CharacterCreate } from '../CharacterCreate';
import { StyledContainer } from './CharactersList.styled';

const CharactersList = () => {
	const characters = useObservable(rage.characterSelection.getCharacters());
	const [openCharacterIndex, setOpenCharacterIndex] = useState<number | null>(
		null,
	);
	const pricePerCharacter = useObservable(
		rage.characterSelection.getPricePerCharacter(),
	);
	const maxCharacters = useObservable(
		rage.characterSelection.getMaxCharacters(),
	);

	const showAllCharacters = useCallback(() => {
		setOpenCharacterIndex(null);
		rage.characterSelection
			.setCurrentIndex(-1)
			.catch((err) => rage.notifications.showException(err));
	}, []);

	useEffect(() => {
		const handleDocumentClick = (event: MouseEvent) => {
			const target = event.target as HTMLElement | null;
			if (
				target?.closest(
					'[data-character-select-card], [data-character-select-control]',
				)
			) {
				return;
			}

			showAllCharacters();
		};

		document.addEventListener('click', handleDocumentClick);
		return () => {
			document.removeEventListener('click', handleDocumentClick);
		};
	}, [showAllCharacters]);

	const handleCharacterClick = (index: number) => {
		if (openCharacterIndex !== index) {
			rage.characterSelection
				.setCurrentIndex(index)
				.catch((err) => rage.notifications.showException(err));
		}

		setOpenCharacterIndex(index);
	};

	if (!characters || characters.length === 0) {
		return (
			<StyledContainer>
				{Array.from({ length: maxCharacters || 0 }, (_, index) => {
					const price = pricePerCharacter[index] || 0;
					return (
						<CharacterCreate
							key={`create-${index}`}
							index={index}
							price={price}
						/>
					);
				})}
			</StyledContainer>
		);
	}

	return (
		<StyledContainer>
			{characters.map((character, index) => {
				if (character === null) {
					const price = pricePerCharacter[index] || 0;
					return (
						<CharacterCreate
							key={`create-${index}`}
							index={index}
							price={price}
						/>
					);
				}

				return (
					<Character
						key={character.id}
						id={parseInt(character.id)}
						name={character.name}
						level={character.level}
						isStaff={false}
						balance={character.totalMoneyAmount}
						lastEnteredAt={character.lastEnteredAt}
						banExpiresAt={character.banExpiresAt}
						banReason={character.banReason ?? null}
						isOpen={openCharacterIndex === index}
						onClick={() => handleCharacterClick(index)}
					/>
				);
			})}
		</StyledContainer>
	);
};

export { CharactersList };
